/**
 * Skill Category Data
 * 
 * Organized skill categories for visualizing growth throughout career journey
 * Each category contains related skills that contribute to the overall learning narrative
 */

export const skillCategories = [
  {
    id: "technical-documentation",
    name: "Technical Documentation & Knowledge Transfer",
    description: "Transforming complex technical information into accessible learning materials",
    color: "#00A9E0", // Scientific Cyan
    skills: [
      "Technical Documentation",
      "Educational Materials Development",
      "Process Visualization",
      "Knowledge Base Creation",
      "SOP Development",
      "Troubleshooting Guides",
      "Reference Material Design",
      "Technical Content Strategy"
    ]
  },
  {
    id: "instructional-design",
    name: "Instructional Design & Education",
    description: "Creating structured learning experiences and educational content",
    color: "#FF3E8A", // Mad Magenta
    skills: [
      "Curriculum Development",
      "Workshop Facilitation",
      "Technical Training",
      "Learning Needs Assessment",
      "Educational Content Creation",
      "Learning Progression Design",
      "Instructional Scaffolding",
      "Assessment Development"
    ]
  },
  {
    id: "technical-systems",
    name: "Technical Systems & Automation",
    description: "Developing tools and systems to improve technical processes",
    color: "#57CC99", // Flash Green
    skills: [
      "Workflow Automation",
      "Python Development",
      "VBA/Excel Applications",
      "Process Automation",
      "Database Integration",
      "Analytics Systems",
      "Data Visualization",
      "Technical Infrastructure"
    ]
  },
  {
    id: "visual-communication",
    name: "Visual Communication & Design",
    description: "Using visual elements to communicate complex information",
    color: "#FF9B42", // Experiment Orange
    skills: [
      "Technical Illustrations",
      "Information Graphics",
      "Visual Hierarchy",
      "Digital Asset Management",
      "Process Diagrams",
      "User Interface Design",
      "Visual Storytelling",
      "Multimedia Integration"
    ]
  },
  {
    id: "process-optimization",
    name: "Process Analysis & Optimization",
    description: "Analyzing and improving technical workflows and procedures",
    color: "#8884d8", // Purple
    skills: [
      "Process Mapping",
      "Workflow Analysis",
      "Efficiency Optimization",
      "Quality Control Systems",
      "Technical Specifications",
      "Resource Allocation",
      "Implementation Planning",
      "Performance Metrics"
    ]
  },
  {
    id: "leadership",
    name: "Technical Leadership & Consulting",
    description: "Guiding teams and clients through technical decisions and implementations",
    color: "#2D3142", // Laboratory Dark
    skills: [
      "Technical Consultation",
      "Project Leadership",
      "Team Development",
      "Requirements Gathering",
      "Technical Decision Making",
      "Stakeholder Communication",
      "Innovation Management",
      "Technical Strategy"
    ]
  }
];

/**
 * Skill Growth Timeline Data
 * 
 * Shows the progression of each skill category throughout the career journey
 * Values represent competency level on a scale of 0-100
 */
export const skillGrowthTimeline = [
  {
    period: "2007-2012",
    role: "Early Career",
    "Technical Documentation & Knowledge Transfer": 35,
    "Instructional Design & Education": 25,
    "Technical Systems & Automation": 40,
    "Visual Communication & Design": 65,
    "Process Analysis & Optimization": 30,
    "Technical Leadership & Consulting": 20
  },
  {
    period: "2012-2015",
    role: "Operational Management",
    "Technical Documentation & Knowledge Transfer": 50,
    "Instructional Design & Education": 45,
    "Technical Systems & Automation": 60,
    "Visual Communication & Design": 70,
    "Process Analysis & Optimization": 55,
    "Technical Leadership & Consulting": 50
  },
  {
    period: "2015-2016",
    role: "Technical Documentation Specialist",
    "Technical Documentation & Knowledge Transfer": 70,
    "Instructional Design & Education": 55,
    "Technical Systems & Automation": 65,
    "Visual Communication & Design": 80,
    "Process Analysis & Optimization": 60,
    "Technical Leadership & Consulting": 55
  },
  {
    period: "2016-2017",
    role: "Education Specialist",
    "Technical Documentation & Knowledge Transfer": 75,
    "Instructional Design & Education": 70,
    "Technical Systems & Automation": 65,
    "Visual Communication & Design": 80,
    "Process Analysis & Optimization": 65,
    "Technical Leadership & Consulting": 60
  },
  {
    period: "2017-2019",
    role: "Print & Marketing Leadership",
    "Technical Documentation & Knowledge Transfer": 80,
    "Instructional Design & Education": 75,
    "Technical Systems & Automation": 70,
    "Visual Communication & Design": 85,
    "Process Analysis & Optimization": 75,
    "Technical Leadership & Consulting": 75
  },
  {
    period: "2019-2021",
    role: "Digital Production Specialist",
    "Technical Documentation & Knowledge Transfer": 85,
    "Instructional Design & Education": 80,
    "Technical Systems & Automation": 80,
    "Visual Communication & Design": 90,
    "Process Analysis & Optimization": 85,
    "Technical Leadership & Consulting": 80
  },
  {
    period: "2021-Present",
    role: "Technical Education Specialist",
    "Technical Documentation & Knowledge Transfer": 95,
    "Instructional Design & Education": 90,
    "Technical Systems & Automation": 90,
    "Visual Communication & Design": 95,
    "Process Analysis & Optimization": 90,
    "Technical Leadership & Consulting": 90
  }
];

/**
 * Representative skills for each role in the career journey
 * Shows which skills were most developed in each career phase
 */
export const careerSkillHighlights = {
  "early-career": [
    {
      category: "Visual Communication & Design",
      skills: ["Digital Photography", "Visual Storytelling", "Photoshop"]
    },
    {
      category: "Technical Documentation & Knowledge Transfer",
      skills: ["Product Knowledge Documentation", "Customer Education", "Technical Communication"]
    }
  ],
  "brookstone": [
    {
      category: "Technical Systems & Automation",
      skills: ["VBA/Excel Applications", "Workflow Management Systems", "3D Modeling"]
    },
    {
      category: "Process Analysis & Optimization",
      skills: ["Process Mapping", "Operational Efficiency", "Resource Allocation"]
    },
    {
      category: "Leadership",
      skills: ["Team Development", "Performance Evaluation", "Training Program Creation"]
    }
  ],
  "richmond": [
    {
      category: "Technical Documentation & Knowledge Transfer",
      skills: ["Technical Documentation", "Knowledge Base Creation", "Reference Material Design"]
    },
    {
      category: "Visual Communication & Design",
      skills: ["Technical Illustrations", "Digital Asset Management", "Visual Hierarchies"]
    }
  ],
  "field-organizer": [
    {
      category: "Instructional Design & Education",
      skills: ["Educational Content Creation", "Public Information Design", "Audience Analysis"]
    },
    {
      category: "Visual Communication & Design",
      skills: ["Information Graphics", "Visual Storytelling", "Outreach Materials"]
    }
  ],
  "education-roles": [
    {
      category: "Instructional Design & Education",
      skills: ["Curriculum Development", "Learning Needs Assessment", "Learning Progression Design"]
    },
    {
      category: "Technical Documentation & Knowledge Transfer",
      skills: ["Educational Materials Development", "Documentation Management", "Procedural Documentation"]
    }
  ],
  "staples-management": [
    {
      category: "Technical Leadership & Consulting",
      skills: ["Technical Consultation", "Team Development", "Technical Decision Making"]
    },
    {
      category: "Process Analysis & Optimization",
      skills: ["Workflow Analysis", "Efficiency Optimization", "Performance Metrics"]
    }
  ],
  "staples-digital": [
    {
      category: "Technical Documentation & Knowledge Transfer",
      skills: ["Technical Documentation", "SOP Development", "Troubleshooting Guides"]
    },
    {
      category: "Technical Systems & Automation",
      skills: ["Analytics Systems", "Process Automation", "Data Visualization"]
    },
    {
      category: "Process Analysis & Optimization",
      skills: ["Quality Control Systems", "Technical Specifications", "Process Mapping"]
    }
  ],
  "indigo": [
    {
      category: "Technical Documentation & Knowledge Transfer",
      skills: ["Technical Content Strategy", "Comprehensive Documentation", "Knowledge Transfer Systems"]
    },
    {
      category: "Instructional Design & Education",
      skills: ["Workshop Facilitation", "Technical Training", "Instructional Scaffolding"]
    },
    {
      category: "Technical Systems & Automation",
      skills: ["Python Development", "Workflow Automation", "Database Integration"]
    },
    {
      category: "Technical Leadership & Consulting",
      skills: ["Innovation Management", "Technical Strategy", "Stakeholder Communication"]
    }
  ]
}; 